import React from 'react';
import {Button, Tooltip} from 'antd';
import {FileTextOutlined, LineChartOutlined} from '@ant-design/icons';
import type {HedgeLegRealized, HedgePosition} from '../../../types/hedge';
import {
    BIAS_META,
    CLOSE_REASON_TEXT,
    fmtCompactTime,
    fmtDuration,
    fmtPct,
    fmtPnl,
    fmtPx,
    fmtQty,
    fmtTime,
    LEG_EXIT_REASON_TEXT,
} from './hedgeDisplay';

/** 结束态徽标（与 HedgePage END_STATUSES 对应） */
const COMPLETION_META: Record<string, { text: string; cls: string }> = {
    CLOSED: {text: '已平仓', cls: 'closed'},
    COMPENSATED: {text: '已补偿退出', cls: 'compensated'},
    // 结束 tab 里的 INCOMPLETE 只来自 FAILED（markFailed：无敞口建仓失败）
    INCOMPLETE: {text: '⚠ 建仓失败', cls: 'incomplete'},
};

const fmtAmount = (value?: number): string =>
    null == value ? '--' : `${Number(value).toFixed(2)}U`;

const fmtLegPnl = (value?: number): string =>
    null == value ? '--' : `${Number(value) >= 0 ? '+' : ''}${Number(value).toFixed(2)}U`;

interface Props {
    position: HedgePosition;
    /** 点击卡片 / 研报按钮 → 右侧展开开仓研报 */
    onOpenReport: (position: HedgePosition) => void;
    /** 点击某腿 K 线按钮 → 展开该腿 K 线（结束态读离线快照） */
    onOpenChart: (position: HedgePosition, market: 'SPOT' | 'SWAP') => void;
}

/** 单腿时间展示：开始 → 结束（保护单触发平仓的腿用仓位结束时间兜底，带 ≈ 标记） */
const legTimeText = (leg: HedgeLegRealized): { text: string; full: string } => {
    const start = fmtCompactTime(leg.openedAt);
    const end = fmtCompactTime(leg.closedAt);
    const prefix = leg.closeTimeFallback ? '≈' : '';
    return {
        text: `${start.text} → ${prefix}${end.text}`,
        full: `${start.full} → ${prefix}${end.full}`,
    };
};

/** 单腿均价展示：开仓 → 平仓 */
const legPxText = (leg: HedgeLegRealized): string =>
    `${fmtPx(leg.openAvgPx)} → ${fmtPx(leg.closeAvgPx)}`;

/**
 * 结束态对冲卡片：整体信息（总盈亏/创建→结束/投入/对冲率/平仓原因/研报）+ 分腿明细
 * （主单/对冲单：时间、金额、均价、盈亏、K 线入口）；运行期信息（现价/偏离/敞口/保护单）不展示。
 */
export const HedgeHistoryCard: React.FC<Props> = ({position, onOpenReport, onOpenChart}) => {
    const p = position;
    // 未知完成度：保留原文但按告警色渲染，避免静默落成"已平仓"灰样式
    const completion = COMPLETION_META[p.completionState] ?? {text: p.completionState ?? '--', cls: 'incomplete'};
    const bias = BIAS_META[p.bias ?? ''] ?? {text: p.bias ?? '--', cls: ''};
    const endedAt = p.closedAt ?? p.compensatedAt;
    const hasPnl = null != p.realizedPnlUsdt;
    const pnlCls = !hasPnl ? 'dim' : (Number(p.realizedPnlUsdt) >= 0 ? 'ok' : 'warn');
    const spotIsMain = 'SPOT' === p.mainLeg;

    /** 主单固定左侧、对冲单固定右侧（与运行中卡片一致） */
    const legs: Array<{ leg?: HedgeLegRealized; market: 'SPOT' | 'SWAP' }> = spotIsMain
        ? [{leg: p.spotLeg, market: 'SPOT'}, {leg: p.swapLeg, market: 'SWAP'}]
        : [{leg: p.swapLeg, market: 'SWAP'}, {leg: p.spotLeg, market: 'SPOT'}];

    const legTitle = (market: 'SPOT' | 'SWAP'): string => {
        const isMain = ('SPOT' === market) === spotIsMain;
        const role = isMain ? '主单' : '对冲单';
        return `${role} · ${'SPOT' === market ? '现货买入' : '永续空'}`;
    };
    /** 金额口径：现货腿=建仓名义金额；永续腿=保证金占用（÷杠杆） */
    const amountLabel = (market: 'SPOT' | 'SWAP'): string =>
        'SPOT' === market ? '金额' : '保证金';

    return (
        <div className={`hedge-card hedge-history-card ${completion.cls}`}
             onClick={() => onOpenReport(p)}>
            <div className="hedge-card-head">
                <span className="hedge-card-symbol">{p.symbol}</span>
                <span className={`hedge-bias ${bias.cls}`}>{bias.text}</span>
                <span className={`hedge-completion ${completion.cls}`}>{completion.text}</span>
                <span className="hedge-card-time" title={fmtTime(endedAt)}>{fmtTime(endedAt)}</span>
            </div>

            <div className="hedge-history-pnl">
                <span className="hedge-history-pnl-label"
                      title="已实现盈亏：含开/平仓手续费；新仓快照含资金费">总盈亏</span>
                <span className={`hedge-history-pnl-value ${pnlCls}`}>
                    {hasPnl ? fmtPnl(p.realizedPnlUsdt, p.realizedPnlPct) : '--'}
                </span>
            </div>

            <div className="hedge-history-times">
                <div className="hedge-history-time-row">
                    <span className="hedge-history-time-label">创建</span>
                    <span>{fmtTime(p.createdAt)}</span>
                </div>
                <div className="hedge-history-time-row">
                    <span className="hedge-history-time-label">结束</span>
                    <span>{fmtTime(endedAt)}</span>
                </div>
                <div className="hedge-history-time-row">
                    <span className="hedge-history-time-label">持仓</span>
                    <span>{fmtDuration(p.createdAt, endedAt)}</span>
                </div>
            </div>

            <div className="hedge-legs">
                {legs.map(({leg, market}) => {
                    const time = null == leg ? {text: '--', full: '--'} : legTimeText(leg);
                    const pnlValue = leg?.pnlUsdt;
                    const exitReason = leg?.exitReason ?? ('SPOT' === market ? p.spotExitReason : p.swapExitReason);
                    return (
                        <div className="hedge-leg" key={market}>
                            <div className="hedge-leg-title">
                                <span>{legTitle(market)}</span>
                                <span className="hedge-leg-inst">
                                    {'SPOT' === market ? 'SPOT' : 'SWAP'}
                                </span>
                                <Tooltip title={`${'SPOT' === market ? '现货' : '永续'}K线（离线快照）`}>
                                    <Button size="small" type="text" className="hedge-kline-btn"
                                            aria-label="K线" icon={<LineChartOutlined/>}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onOpenChart(p, market);
                                            }}/>
                                </Tooltip>
                            </div>
                            <div className="hedge-leg-row">
                                <span className="hedge-leg-label">时间</span>
                                <span className="hedge-leg-value" title={time.full}>{time.text}</span>
                            </div>
                            <div className="hedge-leg-row">
                                <span className="hedge-leg-label">{amountLabel(market)}</span>
                                <span className="hedge-leg-value">
                                    {fmtAmount(leg?.amountUsdt)}
                                    {null != leg?.leverage && Number(leg.leverage) > 0
                                        ? `（${Number(leg.leverage)}x）` : ''}
                                </span>
                            </div>
                            <div className="hedge-leg-row">
                                <span className="hedge-leg-label">均价</span>
                                <span className="hedge-leg-value">
                                    {null == leg ? '--' : legPxText(leg)}
                                </span>
                            </div>
                            <div className="hedge-leg-row">
                                <span className="hedge-leg-label">盈亏</span>
                                <span className={`hedge-leg-value${null == pnlValue ? ' dim'
                                    : (Number(pnlValue) >= 0 ? ' ok' : ' warn')}`}>
                                    {fmtLegPnl(pnlValue)}
                                </span>
                            </div>
                            {null != exitReason && (
                                <div className="hedge-leg-row">
                                    <span className="hedge-leg-label">离场</span>
                                    <span className="hedge-leg-value warn">
                                        {LEG_EXIT_REASON_TEXT[exitReason] ?? exitReason}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="hedge-card-foot">
                <span title="总投入">投入 {fmtQty(p.totalUsdt)}U</span>
                <span title="目标对冲比例">对冲率 {fmtPct(p.hedgeRatio)}</span>
                {null != p.closeReason && (
                    <span title="平仓原因">原因 {CLOSE_REASON_TEXT[p.closeReason] ?? p.closeReason}</span>
                )}
            </div>

            <div className="hedge-history-actions" onClick={(e) => e.stopPropagation()}>
                <Button size="small" type="link" icon={<FileTextOutlined/>}
                        disabled={null == p.reportId}
                        onClick={() => onOpenReport(p)}>
                    {null == p.reportId ? '无关联研报' : `查看研报 #${p.reportId}`}
                </Button>
            </div>
        </div>
    );
};

export default HedgeHistoryCard;
